import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { PageHeading, NtIcon } from '../components/NotionUI';
import { getMeetings, getMeetingCapabilities, deleteMeeting, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtDateTime } from '../utils/format';
import SearchBox from '../components/SearchBox';
import Highlight from '../components/Highlight';
import { searchTerms, matchesAllTerms } from '../utils/search';

export const STATUS_TONE = { ready: 'ok', processing: 'busy', failed: 'bad', draft: 'idle' };

function Meetings({ user, onLogout }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Ricerca: più parole separate da spazio, devono comparire tutte (AND), nel testo che si vede (non nelle date).
  const terms = searchTerms(search);
  const summaryOf = (m) => (m.status === 'ready' && m.summary ? m.summary : '');
  const stageOf = (m) => (m.status === 'processing' ? `${t(`meetings.stage_${m.stage || 'summarizing'}`)}…` : '');
  const actionsOf = (m) => (m.actions.length > 0 ? t('meetings.actionsCount', { count: m.actions.length }) : '');
  const matchesMeeting = (m) => matchesAllTerms(
    [m.title, m.participants || '', actionsOf(m), summaryOf(m), stageOf(m), t(`meetings.status_${m.status}`)].join(' '),
    terms
  );
  const visibleMeetings = meetings.filter(matchesMeeting);

  const load = async () => {
    try {
      const data = await getMeetings();
      setMeetings(data.meetings || []);
    } catch (err) {
      setError(errorMessage(err, t('meetings.loadError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getMeetingCapabilities().then(setCaps).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le riunioni in elaborazione si aggiornano da sole.
  useEffect(() => {
    if (!meetings.some((m) => m.status === 'processing')) return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetings]);

  const remove = async (e, meeting) => {
    e.stopPropagation();
    if (!window.confirm(t('meetings.deleteConfirm', { title: meeting.title }))) return;
    try {
      await deleteMeeting(meeting.id);
      setMeetings((prev) => prev.filter((m) => m.id !== meeting.id));
    } catch (err) {
      setError(errorMessage(err, t('meetings.deleteError')));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="mic" title={t('meetings.title')} subtitle={t('meetings.subtitle')} />

        <div className="tp-toolbar">
          <SearchBox value={search} onChange={setSearch} placeholder={t('meetings.search')} title={t('meetings.searchHint')} />
          <span className="nt-muted">{loading ? '' : t('meetings.count', { count: visibleMeetings.length })}</span>
          <button className="btn" type="button" onClick={() => navigate('/meetings/new')}>
            + {t('meetings.new')}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {caps && (
          <div className="mt-engines">
            <span className={'mt-engine' + (caps.ai ? ' mt-engine-on' : '')}>
              <i aria-hidden="true" /> {caps.ai ? t('meetings.engineAi', { model: caps.model }) : t('meetings.engineSimple')}
            </span>
            <span className={'mt-engine' + (caps.transcription_reachable ? ' mt-engine-on' : '')}>
              <i aria-hidden="true" /> {caps.transcription_reachable ? t('meetings.engineAudioOn') : t('meetings.engineAudioOff')}
            </span>
          </div>
        )}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : meetings.length === 0 ? (
          <div className="au-empty">
            <span className="au-empty-icon" aria-hidden="true"><NtIcon name="mic" size={26} /></span>
            <p>{t('meetings.empty')}</p>
            <button className="btn" type="button" onClick={() => navigate('/meetings/new')}>{t('meetings.new')}</button>
          </div>
        ) : (
          <div className="mt-list">
            {visibleMeetings.length === 0 && <p className="nt-empty">{t('meetings.noResults')}</p>}
            {visibleMeetings.map((m) => (
              <div
                key={m.id}
                role="link"
                tabIndex={0}
                className="mt-item"
                onClick={() => navigate(`/meetings/${m.id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/meetings/${m.id}`)}
              >
                <span className="mt-item-icon" aria-hidden="true"><NtIcon name="mic" size={18} /></span>
                <div className="mt-item-main">
                  <div className="mt-item-title"><Highlight text={m.title} terms={terms} /></div>
                  <div className="mt-item-meta">
                    <span>{fmtDateTime(m.meeting_date || m.created_at)}</span>
                    {m.participants && <span><Highlight text={m.participants} terms={terms} /></span>}
                    {m.actions.length > 0 && <span><Highlight text={actionsOf(m)} terms={terms} /></span>}
                  </div>
                  {m.status === 'ready' && m.summary && <p className="mt-item-summary"><Highlight text={m.summary} terms={terms} /></p>}
                  {m.status === 'processing' && <p className="mt-item-summary"><Highlight text={stageOf(m)} terms={terms} /></p>}
                </div>
                <span className={`mt-status mt-status-${STATUS_TONE[m.status] || 'idle'}`}><Highlight text={t(`meetings.status_${m.status}`)} terms={terms} /></span>
                <button type="button" className="tp-icon tp-icon-danger" onClick={(e) => remove(e, m)} title={t('common.delete')} aria-label={t('common.delete')}>
                  <NtIcon name="trash" size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Meetings;
